import React, { useState, useEffect } from 'react';
import confetti from 'canvas-confetti';
import { Button } from 'react-bootstrap';

// Sound effects with visuals
const soundEffects = [
  { name: 'Bell', emoji: '🔔', sound: '/sounds/bell.mp3' },
  { name: 'Whistle', emoji: '🎵', sound: '/sounds/whistle.mp3' },
  { name: 'Clap', emoji: '👏', sound: '/sounds/clap.mp3' },
  { name: 'Laugh', emoji: '😂', sound: '/sounds/laugh.mp3' },
  { name: 'Splash', emoji: '💦', sound: '/sounds/splash.mp3' },
  { name: 'Pop', emoji: '🎈', sound: '/sounds/pop.mp3' },
  { name: 'Beep', emoji: '📢', sound: '/sounds/beep.mp3' },
  { name: 'Swoosh', emoji: '💨', sound: '/sounds/swoosh.mp3' }
];

export const SoundEffectsGame = ({ handleClose, onGameComplete }) => {
  const [targetSound, setTargetSound] = useState(null);
  const [playedSounds, setPlayedSounds] = useState([]);
  const [score, setScore] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const correctSound = new Audio('/sounds/correct.mp3');
  const errorSound = new Audio('/sounds/error.mp3');
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);

  useEffect(() => {
    const savedVolume = localStorage.getItem('tapGameVolume');
    if (savedVolume !== null) {
      setVolume(parseFloat(savedVolume));
    }
  }, []);

  useEffect(() => {
    const initTimeout = setTimeout(() => {
      playNewSound();
    }, 500);
    return () => clearTimeout(initTimeout);
  }, []);

  // Speech instruction - only on first load
  useEffect(() => {
    if (targetSound && !isMuted && !isPlaying && playedSounds.length === 0) {
      const speakTimeout = setTimeout(() => {
        const utterance = new SpeechSynthesisUtterance(
          `Tap the ${targetSound.name} sound`
        );
        utterance.rate = 0.8;
        utterance.volume = volume;
        speechSynthesis.speak(utterance);
      }, 3000);

      return () => clearTimeout(speakTimeout);
    }
  }, [targetSound, isMuted, volume, isPlaying, playedSounds.length]);

  const playNewSound = (currentPlayed = playedSounds) => {
    const availableSounds = soundEffects.filter(s => !currentPlayed.includes(s.name));
    if (availableSounds.length === 0) {
      const stars = 3;
      const finalScore = score;
      confetti({ particleCount: 250, spread: 100 });
      setTimeout(() => {
        if (onGameComplete) {
          onGameComplete(stars, finalScore);
        }
        if (handleClose) {
          handleClose();
        }
      }, 2500);
      return;
    }

    const next = availableSounds[Math.floor(Math.random() * availableSounds.length)];
    setTargetSound(next);
    
    setTimeout(() => {
      playSound(next.sound, next.name);
    }, 800);
  };

  const playSound = (soundPath, soundName = '') => {
    if (isMuted) return;
    
    setIsPlaying(true);
    const audio = new Audio(soundPath);
    audio.volume = volume;
    
    // Limit clap sound to 1 second
    if (soundName === 'Clap') {
      audio.onended = () => setIsPlaying(false);
      audio.play().catch(err => {
        console.log('Audio play error:', err);
        setIsPlaying(false);
      });
      
      setTimeout(() => {
        if (!audio.paused) {
          audio.pause();
          audio.currentTime = 0;
          setIsPlaying(false);
        }
      }, 1000);
    } else {
      audio.onended = () => setIsPlaying(false);
      audio.onerror = (err) => {
        console.log('Audio play failed:', err);
        setIsPlaying(false);
      };
      
      audio.play().catch(err => {
        console.log('Audio play error:', err);
        setIsPlaying(false);
      });
    }
  };

  const handleSoundClick = (sound) => {
    if (sound.name === targetSound.name) {
      if (!isMuted) {
        correctSound.volume = volume;
        correctSound.play();
      }
      
      playSound(sound.sound, sound.name);
      const updatedPlayed = [...playedSounds, sound.name];
      setScore(score + 15);
      setPlayedSounds(updatedPlayed);
      
      setTimeout(() => {
        playNewSound(updatedPlayed);
      }, 1500);
    } else {
      if (!isMuted) {
        errorSound.volume = volume;
        errorSound.play();
      }
    }
  };

  const replayTargetSound = () => {
    if (targetSound) {
      playSound(targetSound.sound);
    }
  };

  return (
    <div className="tap-letter-board-bg">
      <div className="tap-header-bar mb-1">
        <div className="tap-score-box">
          <span role="img" aria-label="score" style={{ fontSize: 22, marginRight: 6 }}>🎵</span>
          <span className="tap-score-label">Score</span>
          <span className="tap-score-value">{playedSounds.length}/{soundEffects.length}</span>
        </div>
        <div className="tap-letter-target">
          <span className="tap-letter-label">
            {isPlaying ? '🔊 Listening...' : '🎧 Which sound?'}
          </span>
          <span className="tap-letter-value">{targetSound?.name || ''}</span>
        </div>
        <div className="tap-volume-box">
          <span role="img" aria-label="volume" style={{ fontSize: 20, marginRight: 4 }}>🔊</span>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={volume}
            onChange={(e) => {
              const newVol = parseFloat(e.target.value);
              setVolume(newVol);
              localStorage.setItem('tapGameVolume', newVol);
            }}
            style={{ width: '90px', verticalAlign: 'middle' }}
          />
          <span className="tap-volume-value">{Math.round(volume * 100)}%</span>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary ms-2"
            style={{ borderRadius: 12, padding: '2px 8px', marginLeft: 8 }}
            title="Replay Sound"
            onClick={replayTargetSound}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 20, verticalAlign: 'middle' }}>replay</span>
          </button>
        </div>
      </div>

      <div className="tap-letter-btn-grid">
        {soundEffects.map((sound) => (
          <button
            key={sound.name}
            className={`btn m-1 letter-btn ${playedSounds.includes(sound.name) ? 'btn-success selected-correct' : 'btn-outline-primary'}`}
            onClick={() => !playedSounds.includes(sound.name) && handleSoundClick(sound)}
            disabled={playedSounds.includes(sound.name)}
            style={{
              width: '100px',
              height: '110px',
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <span style={{ fontSize: '3rem' }}>{sound.emoji}</span>
            <span style={{ fontWeight: 600, fontSize: '0.9rem', marginTop: 4 }}>{sound.name}</span>
          </button>
        ))}
      </div>

      <style>{`
        .tap-letter-board-bg {
          background: url('/image/board.png'), linear-gradient(135deg,rgba(62, 62, 62, 0.3) 0%,rgba(15, 15, 14, 0.41) 100%);
          background-size: cover, cover;
          background-position: center, center;
          border-radius: 32px;
          box-shadow: 0 6px 32px #e0c68a33;
          padding: 32px 12px 24px 12px;
          margin: 0 auto;
          max-width: 900px;
          width: 98%;
          min-height: 400px;
          display: flex;
          flex-direction: column;
          align-items: center;
          position: relative;
          z-index: 2;
          animation: board-pop 0.5s cubic-bezier(.68,-0.55,.27,1.55);
        }
        @keyframes board-pop {
          0% { transform: scale(0.9); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
        .tap-header-bar {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-radius: 20px;
          box-shadow: 0 2px 8px rgba(224,198,138,0.10);
          padding: 1px 18px 18px 18px;
          margin-bottom: 18px;
          gap: 10px;
        }
        .tap-score-box,
        .tap-volume-box {
          display: flex;
          align-items: center;
          border-radius: 14px;
          padding: 6px 14px;
          font-weight: 700;
          font-size: 1.1rem;
          color: #b8860b;
          background: rgba(255, 255, 255, 0.86);
          min-width: 110px;
          box-shadow: 0 1px 4px rgba(224,198,138,0.10);
        }
        .tap-score-label {
          margin-right: 6px;
          font-size: 1rem;
          color: #b8860b;
        }
        .tap-score-value {
          font-size: 1.1rem;
          color: #1976d2;
          margin-left: 4px;
          font-weight: 700;
        }
        .tap-letter-target {
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(224,198,138,0.92);
          border-radius: 18px;
          padding: 8px 32px;
          box-shadow: 0 1px 4px rgba(224,198,138,0.10);
          margin: 0 18px;
          min-width: 180px;
          flex: 1;
        }
        .tap-letter-label {
          font-size: 1.1rem;
          color: #3e2723;
          margin-right: 8px;
          font-weight: 600;
        }
        .tap-letter-value {
          font-size: 1.5rem;
          color: #1976d2;
          font-weight: 900;
          text-shadow: 1px 1px 8px #fff, 0 0 8px #e0c68a;
        }
        .tap-volume-value {
          margin-left: 8px;
          font-size: 1rem;
          color: #1976d2;
          font-weight: 700;
          min-width: 36px;
          display: inline-block;
          text-align: right;
        }
        .tap-letter-btn-grid {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          align-items: center;
          width: 100%;
          min-height: 180px;
          margin-top: 10px;
          gap: 8px;
        }
        .letter-btn {
          padding: 0;
          border-radius: 18px !important;
          background: linear-gradient(135deg,rgb(250, 248, 240) 0%,rgb(248, 228, 166) 100%) !important;
          box-shadow: 0 2px 8px rgba(0,0,0,0.10);
          border: 2px solid rgba(223, 214, 192, 0.65) !important;
          transition: transform 0.1s, box-shadow 0.2s;
        }
        .letter-btn:hover {
          transform: translateY(-5px) scale(1.05);
        }
        .selected-correct {
          background: #000000 !important;
          border-color: rgb(0, 245, 253) !important;
          color: #fff !important;
          box-shadow: 0 0 16px 2px rgba(122, 255, 244, 0.6);
        }
        @media (max-width: 700px) {
          .tap-header-bar {
            flex-direction: column;
            gap: 10px;
            padding-bottom: 10px;
          }
          .tap-letter-target {
            min-width: 0;
            width: 100%;
            padding: 8px 0;
            margin: 8px 0;
          }
          .letter-btn {
            width: 80px !important;
            height: 90px !important;
          }
        }
        @media (max-width: 480px) {
          .tap-letter-board-bg {
            padding: 10px;
          }
          .letter-btn {
            width: 70px !important;
            height: 80px !important;
          }
        }
      `}</style>
    </div>
  );
};
